import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { Amplify, Storage } from 'aws-amplify';
import { Authenticator } from '@aws-amplify/ui-react';
import '@aws-amplify/ui-react/styles.css';

// Import components
import Navigation from './components/Navigation';
import ClientFileBrowser from './components/ClientFileBrowser';
import ClientUploader from './components/ClientUploader';
import TeamInvitation from './components/TeamInvitation';
import AdminDashboard from './components/AdminDashboard';
import AdminUploader from './components/AdminUploader';
import ClientManager from './components/ClientManager';
import FolderSync from './components/FolderSync';
import Settings from './components/Settings';
import PowerBIViewer from './components/PowerBIViewer';
import ClientPreview from './components/ClientPreview';
import UserManagement from './components/UserManagement';
import TaskManager from './components/TaskManager';

// Import configurations
import clientDB from './config/clientDatabase';
import ActivityTracker from './utils/activityTracker';
import './App.css';

// AWS configuration
const awsConfig = {
  Auth: {
    region: 'us-east-1',
    userPoolId: 'us-east-1_PLwDGNorY',
    userPoolWebClientId: '4f509ks2vlvue22pummbjvt3t8',
    identityPoolId: 'us-east-1:206af41a-c7e1-45c3-99bd-b9168d0b8e1b',
    mandatorySignIn: true,
    authenticationFlowType: 'USER_SRP_AUTH'
  },
  Storage: {
    AWSS3: {
      bucket: 'report-sharing-platform-reports-038462795459',
      region: 'us-east-1',
      identityPoolId: 'us-east-1:206af41a-c7e1-45c3-99bd-b9168d0b8e1b'
    }
  },
  CloudFront: {
    domain: 'reports.1209digital.com'
  }
};

Amplify.configure(awsConfig);

// Main App component
const AppContent = ({ signOut, user }) => {
  const [userCompany, setUserCompany] = useState(null);

  // Get user details and company
  useEffect(() => {
    if (user) {
      const email = user.attributes?.email || user.username;
      let userData = clientDB.getUserWithCompany(email);

      if (!userData) {
        // New signup - create pending user
        const userName = user.attributes?.name || user.attributes?.given_name || email.split('@')[0];

        // Check if this is the first user (they should be auto-approved as admin)
        const allUsers = clientDB.getAllUsers();
        const isFirstUser = allUsers.length === 1; // Only the default admin exists

        // Create a default company for new signups if they're not the admin
        const companyName = email.split('@')[1]?.split('.')[0] || 'default-company';
        const companyId = clientDB.createCompany({
          name: companyName.charAt(0).toUpperCase() + companyName.slice(1),
          primaryColor: '#1e40af',
          secondaryColor: '#3b82f6',
          maxTeamMembers: 5,
          fileTypes: ['pdf', 'doc', 'xls', 'ppt'],
          storageLimit: 5368709120, // 5GB
        });

        // Create the user with pending status
        clientDB.createUser({
          email: email,
          name: userName,
          companyId: companyId,
          role: isFirstUser ? 'owner' : 'admin',
          approvalStatus: isFirstUser ? 'approved' : 'pending'
        });

        // Reload user data
        userData = clientDB.getUserWithCompany(email);
      }

      if (userData) {
        // Check approval status
        if (userData.approvalStatus === 'approved') {
          setUserCompany(userData.company);

          // Track login activity
          const userName = user.attributes?.name || userData.name || email.split('@')[0];
          ActivityTracker.trackLogin(
            email,
            userName,
            userData.company.name,
            userData.company.type
          );
        } else {
          // User exists but not approved
          setUserCompany(null);
        }
      }
    }
  }, [user]);

  // Apply company branding
  useEffect(() => {
    if (userCompany?.branding) {
      const root = document.documentElement;
      if (userCompany.branding.primaryColor) {
        root.style.setProperty('--primary-color', userCompany.branding.primaryColor);
      }
      if (userCompany.branding.secondaryColor) {
        root.style.setProperty('--secondary-color', userCompany.branding.secondaryColor);
      }
    }
  }, [userCompany]);

  return (
    <div className="App">
      {userCompany && (
        <Navigation
          user={user}
          company={userCompany}
          signOut={signOut}
        />
      )}

      <main className="app-main">
        <Routes>
          {/* Public route for accepting invitations */}
          <Route path="/invite/:inviteId" element={
            <InviteAccept />
          } />

          {/* Protected routes */}
          {userCompany ? (
            <>
              {/* Admin routes */}
              {userCompany.type === 'admin' ? (
                <>
                  <Route path="/dashboard" element={
                    <AdminDashboard user={user} company={userCompany} />
                  } />
                  <Route path="/clients" element={
                    <ClientManager user={user} company={userCompany} />
                  } />
                  <Route path="/users" element={
                    <UserManagement user={user} company={userCompany} />
                  } />
                  <Route path="/tasks" element={
                    <TaskManager user={user} company={userCompany} />
                  } />
                  <Route path="/sync" element={
                    <FolderSync user={user} company={userCompany} />
                  } />
                  <Route path="/upload" element={
                    <AdminUploader user={user} company={userCompany} />
                  } />
                  <Route path="/reports" element={
                    <PowerBIViewer user={user} company={userCompany} />
                  } />
                  <Route path="/client-preview" element={
                    <ClientPreview />
                  } />
                  <Route path="/files" element={<Navigate to="/dashboard" replace />} />
                  <Route path="/team" element={<Navigate to="/dashboard" replace />} />
                  <Route path="/" element={<Navigate to="/dashboard" replace />} />
                </>
              ) : (
                <>
                  {/* Client routes */}
                  <Route path="/files" element={
                    <ClientFileBrowser company={userCompany} />
                  } />
                  <Route path="/upload" element={
                    <ClientUploader company={userCompany} />
                  } />
                  <Route path="/team" element={
                    <TeamInvitation user={user} company={userCompany} />
                  } />
                  <Route path="/reports" element={
                    <PowerBIViewer user={user} company={userCompany} />
                  } />
                  <Route path="/dashboard" element={<Navigate to="/reports" replace />} />
                  <Route path="/" element={<Navigate to="/reports" replace />} />
                </>
              )}

              {/* Shared routes */}
              <Route path="/settings" element={
                <Settings user={user} company={userCompany} />
              } />
            </>
          ) : (
            // User not approved or not in database - show appropriate message
            <Route path="*" element={
              <div className="setup-screen">
                {(() => {
                  const email = user?.attributes?.email || user?.username;
                  const userData = clientDB.getUser(email);

                  if (userData?.approvalStatus === 'pending') {
                    return (
                      <>
                        <h2>⏳ Account Pending Approval</h2>
                        <p>Your account has been created and is awaiting administrator approval.</p>
                        <p>You will be able to access the portal once an administrator approves your account.</p>
                        <p>Email: {email}</p>
                      </>
                    );
                  } else if (userData?.approvalStatus === 'rejected') {
                    return (
                      <>
                        <h2>❌ Account Access Denied</h2>
                        <p>Your account request has been reviewed and was not approved.</p>
                        <p>Please contact your administrator for more information.</p>
                        <p>Email: {email}</p>
                      </>
                    );
                  } else {
                    return (
                      <>
                        <h2>Account Setup Required</h2>
                        <p>Please contact your administrator to complete your account setup.</p>
                        <p>Email: {email}</p>
                      </>
                    );
                  }
                })()}
                <button onClick={signOut} className="signout-button">
                  Sign Out
                </button>
              </div>
            } />
          )}
        </Routes>
      </main>
    </div>
  );
};

function App() {
  return (
    <Router>
      <Authenticator
        formFields={{
          signUp: {
            email: {
              label: 'Email',
              placeholder: 'Enter your email',
              isRequired: true,
              order: 1
            },
            password: {
              label: 'Password',
              placeholder: 'Enter your password',
              isRequired: true,
              order: 2
            },
            name: {
              label: 'Full Name',
              placeholder: 'Enter your full name',
              isRequired: true,
              order: 3
            }
          },
          signIn: {
            username: {
              label: 'Email',
              placeholder: 'Enter your email',
              isRequired: true
            },
            password: {
              label: 'Password',
              placeholder: 'Enter your password',
              isRequired: true
            }
          }
        }}
        components={{
          Header() {
            return (
              <div style={{
                padding: '8px 20px 16px',
                textAlign: 'center',
                background: 'white',
                color: '#1e293b',
                borderBottom: '1px solid #e2e8f0'
              }}>
                <img
                  src="/1209-digital-logo.svg"
                  alt="1209 Digital"
                  style={{
                    height: '52px',
                    width: 'auto',
                    marginBottom: '4px'
                  }}
                />
                <h1 style={{
                  fontSize: '1.5rem',
                  fontWeight: '700',
                  color: '#1e40af',
                  margin: '0 0 4px 0'
                }}>Report Portal</h1>
                <p style={{
                  fontSize: '0.9rem',
                  color: '#64748b',
                  margin: '0'
                }}>Secure document sharing platform</p>
              </div>
            );
          }
        }}
      >
        {({ signOut, user }) => (
          <AppContent signOut={signOut} user={user} />
        )}
      </Authenticator>
    </Router>
  );
}

// Invitation Accept Component
const InviteAccept = () => {
  const { inviteId } = useParams();
  const [invitation, setInvitation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userDetails, setUserDetails] = useState({
    name: '',
    password: '',
    confirmPassword: ''
  });

  useEffect(() => {
    loadInvitation();
  }, [inviteId]);

  const loadInvitation = () => {
    try {
      const invite = clientDB.getInvitation(inviteId);
      if (!invite) {
        setError('Invitation not found or expired');
      } else if (invite.status !== 'pending') {
        setError('This invitation has already been used or expired');
      } else {
        setInvitation(invite);
      }
    } catch (err) {
      setError('Error loading invitation');
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptInvitation = async () => {
    if (!userDetails.name || !userDetails.password || !userDetails.confirmPassword) {
      alert('Please fill in all fields');
      return;
    }

    if (userDetails.password !== userDetails.confirmPassword) {
      alert('Passwords do not match');
      return;
    }

    if (userDetails.password.length < 8) {
      alert('Password must be at least 8 characters long');
      return;
    }

    try {
      // Accept the invitation in the database
      const result = clientDB.acceptInvitation(inviteId, {
        name: userDetails.name
      });

      if (result.success) {
        alert(`Welcome to ${invitation.companyId}! Your account has been created.\n\nYou can now sign up with your email: ${invitation.inviteeEmail}\n\nPlease complete your Cognito signup to access the portal.`);

        // Redirect to login
        window.location.href = '/';
      } else {
        setError(result.error);
      }
    } catch (err) {
      console.error('Error accepting invitation:', err);
      setError('Error accepting invitation. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="invite-accept-container">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading invitation...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="invite-accept-container">
        <div className="error-state">
          <h2>⚠️ Invitation Error</h2>
          <p>{error}</p>
          <button onClick={() => window.location.href = '/'} className="btn-primary">
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  const company = clientDB.getCompany(invitation.companyId);

  return (
    <div className="invite-accept-container">
      <div className="invite-card">
        <div className="invite-header">
          <h1>🎉 You're Invited!</h1>
          <p>Join <strong>{company?.name}</strong> on their secure portal</p>
        </div>

        <div className="invite-details">
          <div className="detail-item">
            <span className="label">Email:</span>
            <span className="value">{invitation.inviteeEmail}</span>
          </div>
          <div className="detail-item">
            <span className="label">Role:</span>
            <span className="value">{invitation.role}</span>
          </div>
          <div className="detail-item">
            <span className="label">Company:</span>
            <span className="value">{company?.name}</span>
          </div>
        </div>

        <div className="invite-form">
          <h3>Complete Your Setup</h3>

          <div className="form-group">
            <label>Full Name</label>
            <input
              type="text"
              value={userDetails.name}
              onChange={(e) => setUserDetails({...userDetails, name: e.target.value})}
              placeholder="Enter your full name"
            />
          </div>

          <div className="form-group">
            <label>Password</label>
            <input
              type="password"
              value={userDetails.password}
              onChange={(e) => setUserDetails({...userDetails, password: e.target.value})}
              placeholder="Create a secure password"
            />
            <small>Must be at least 8 characters long</small>
          </div>

          <div className="form-group">
            <label>Confirm Password</label>
            <input
              type="password"
              value={userDetails.confirmPassword}
              onChange={(e) => setUserDetails({...userDetails, confirmPassword: e.target.value})}
              placeholder="Confirm your password"
            />
          </div>

          <button onClick={handleAcceptInvitation} className="btn-primary full-width">
            Accept Invitation & Join Portal
          </button>
        </div>

        <div className="invite-footer">
          <p><small>After accepting, you'll need to complete your AWS Cognito signup to access the portal.</small></p>
        </div>
      </div>
    </div>
  );
};

const Support = ({ user, company }) => (
  <div className="component-placeholder">
    <h2>Support</h2>
    <p>Get help and contact support</p>
  </div>
);

export default App;